import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSeoMeta } from "@unhead/react";
import toast from "react-hot-toast";
import {Bell, Loader2, MailCheck} from 'lucide-react';
import { lumi } from "@/lib/lumi";
import { useAuth } from "@/hooks/useAuth";

interface AlertRecord {
  _id: string;
  email?: string;
  mineral?: string;
  type?: string;
  experience?: string;
  active?: boolean;
}

const MINERALS = ["All", "Gold", "Copper", "Coal", "Iron Ore", "General"];
const TYPES = ["All", "FIFO", "Residential", "Remote"];
const EXPERIENCE = ["All", "Entry Level", "Mid Level", "Senior", "Supervisor"];

export default function Alerts() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mineral, setMineral] = useState("All");
  const [type, setType] = useState("All");
  const [experience, setExperience] = useState("All");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<AlertRecord[]>([]);

  useSeoMeta({
    title: "Mining Job Email Alerts | MineralJobs Board",
    description:
      "Get automated email notifications when new FIFO mine jobs, mineral careers, and entry level mining jobs are posted on MineralJobs Board.",
  });

  const loadAlerts = async () => {
    if (!user?.userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { list } = await lumi.entities.job_alerts.list({
        filter: { userId: user.userId },
        sort: { createdAt: -1 },
      });
      setAlerts((list || []) as AlertRecord[]);
    } catch (err) {
      console.error("Failed to load alerts:", err);
      toast.error("Could not load your job alerts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthenticated || !user?.email || !user?.userId) {
      navigate("/auth?redirect=/alerts&mode=register");
      return;
    }
    setSaving(true);
    const now = new Date().toISOString();
    try {
      await lumi.entities.job_alerts.create({
        email: user.email,
        userId: user.userId,
        mineral,
        type,
        experience,
        active: true,
        creator: user.userId,
        createdAt: now,
        updatedAt: now,
      });
      toast.success("Job alert armed. You will be emailed when matching roles go live.");
      await loadAlerts();
    } catch (err) {
      console.error("Create alert failed:", err);
      toast.error("Could not save this alert. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleAlert = async (alert: AlertRecord) => {
    try {
      await lumi.entities.job_alerts.update(alert._id, {
        active: !alert.active,
        updatedAt: new Date().toISOString(),
      });
      toast.success(alert.active ? "Alert paused." : "Alert resumed.");
      await loadAlerts();
    } catch (err) {
      console.error("Toggle alert failed:", err);
      toast.error("Could not update this alert.");
    }
  };

  const removeAlert = async (id: string) => {
    try {
      await lumi.entities.job_alerts.delete(id);
      toast.success("Alert removed.");
      await loadAlerts();
    } catch (err) {
      console.error("Delete alert failed:", err);
      toast.error("Could not delete this alert.");
    }
  };

  return (
    <main>
      <div className="min-h-screen pt-16" style={{ background: "#111827" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} style={{ color: "#f97316" }} />
            <span className="text-xs uppercase tracking-wider" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>
              Automated dispatch
            </span>
          </div>
          <h1 className="text-4xl font-black text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Mining job email alerts
          </h1>
          <p className="text-gray-400 max-w-2xl mb-8">
            When a new listing is posted, MineralJobs Board emails matching registered subscribers. Choose mineral, roster, and experience filters so you only hear about roles you want.
          </p>

          {!isAuthenticated ? (
            <div className="rounded-2xl border border-orange-500/20 p-6 mb-8" style={{ background: "rgba(249,115,22,0.08)" }}>
              <p className="text-white font-semibold mb-2">Sign in required</p>
              <p className="text-sm text-gray-400 mb-4">
                Emails can only be sent to registered MineralJobs Board accounts. Create an account, then arm your alert.
              </p>
              <Link
                to="/auth?redirect=/alerts&mode=register"
                className="inline-flex px-5 py-2.5 rounded-xl font-semibold text-white"
                style={{ background: "linear-gradient(135deg, #f97316, #ea580c)" }}
              >
                Create account
              </Link>
            </div>
          ) : null}

          <form onSubmit={handleSubscribe} className="rounded-2xl border border-white/10 p-6 mb-8" style={{ background: "#1a1f2e" }}>
            <div className="grid sm:grid-cols-3 gap-4 mb-5">
              <Field label="Mineral">
                <select className={inputCls} value={mineral} onChange={(e) => setMineral(e.target.value)}>
                  {MINERALS.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </Field>
              <Field label="Roster">
                <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
                  {TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </Field>
              <Field label="Experience">
                <select className={inputCls} value={experience} onChange={(e) => setExperience(e.target.value)}>
                  {EXPERIENCE.map((item) => <option key={item} value={item}>{item}</option>)}
                </select>
              </Field>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #f97316, #ea580c)" }}
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <MailCheck size={16} />}
              Arm email alert
            </button>
          </form>

          <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ background: "#1a1f2e" }}>
            <div className="px-5 py-4 border-b border-white/5">
              <h2 className="text-white font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Your alerts</h2>
            </div>
            {loading ? (
              <div className="flex items-center gap-2 px-5 py-10 text-gray-400">
                <Loader2 size={16} className="animate-spin" /> Loading alerts...
              </div>
            ) : alerts.length === 0 ? (
              <p className="px-5 py-10 text-sm text-gray-500">No alerts yet. Arm one above to get notified on new mining jobs.</p>
            ) : (
              <ul>
                {alerts.map((alert) => (
                  <li key={alert._id} className="px-5 py-4 border-b border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-white text-sm font-medium">
                        {alert.mineral} · {alert.type} · {alert.experience}
                      </p>
                      <p className="text-xs text-gray-500" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        {alert.email} · {alert.active ? "LIVE" : "PAUSED"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => toggleAlert(alert)}
                        className="px-3 py-1.5 rounded-lg text-xs text-gray-300 hover:bg-white/5"
                      >
                        {alert.active ? "Pause" : "Resume"}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeAlert(alert._id)}
                        className="px-3 py-1.5 rounded-lg text-xs text-red-400 hover:bg-white/5"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

const inputCls =
  "w-full px-3 py-2.5 rounded-lg bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-orange-500/60";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
        {label}
      </label>
      {children}
    </div>
  );
}
